import { useCallback, useEffect, useRef, useState } from "react";
import { listClientCases } from "@/features/cases/api";
import type { Case, CaseFilter } from "@/features/cases/types";
import { ApiError } from "@/lib/api";

interface UseCasesResult {
  cases: Case[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetches a client's cases whenever the client id changes. Same guards as
 * `use-client-records.ts`: `lastKeyRef` collapses StrictMode's dev
 * double-invoke, `requestIdRef` drops out-of-order responses.
 */
export function useCases(clientId: string, filter: CaseFilter = "all"): UseCasesResult {
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const lastKeyRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const key = `${reloadKey}|${clientId}|${filter}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;

    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    listClientCases(clientId, filter)
      .then((result) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setCases(result);
      })
      .catch((err) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setCases([]);
        setError(err instanceof ApiError ? err.message : "Failed to load cases.");
      })
      .finally(() => {
        if (mountedRef.current && requestIdRef.current === reqId) setLoading(false);
      });
  }, [clientId, filter, reloadKey]);

  return { cases, loading, error, refetch };
}
